import { REMINDERS_PAUSED_REASONS, type RemindersPausedReason } from '@shared/core/billing'
import { FormattedText } from 'src/modules/core/components'
import type {
  PausedPlanCardDateRowProps,
  PausedPlanCardProps,
  PausedPlanCardUsageProps,
} from './paused-plan-card.interface'
import classes from './paused-plan-card.module.scss'

type PausedPlanCardCopy = { titleId?: string; dateLabelId?: string; ctaId: string }

const PAUSED_PLAN_CARD_COPY: Record<RemindersPausedReason, PausedPlanCardCopy> = {
  [REMINDERS_PAUSED_REASONS.quota]: {
    titleId: 'dashboard.paused.planCard.title',
    ctaId: 'dashboard.paused.planCard.cta',
  },
  [REMINDERS_PAUSED_REASONS.subscriptionEnded]: {
    titleId: 'dashboard.paused.planCard.titleEnded',
    dateLabelId: 'dashboard.paused.planCard.endedLabel',
    ctaId: 'dashboard.paused.planCard.cta',
  },
  [REMINDERS_PAUSED_REASONS.paymentPaused]: {
    dateLabelId: 'dashboard.paused.planCard.pausedLabel',
    ctaId: 'dashboard.paused.planCard.ctaUpdateCard',
  },
}

export function PausedPlanCard({
  reason,
  sentCount,
  limit,
  date,
  planName,
  onResumeClick,
  onUpdateCard,
  isUpdateCardAvailable,
}: PausedPlanCardProps) {
  const copy = PAUSED_PLAN_CARD_COPY[reason]
  const isPaymentPaused = reason === REMINDERS_PAUSED_REASONS.paymentPaused
  const onCtaClick = isPaymentPaused ? onUpdateCard : onResumeClick

  return (
    <div className={classes.card} data-testid="paused-plan-card">
      <div className={classes.header}>
        <span className={classes.title}>{copy.titleId ? <FormattedText id={copy.titleId} /> : planName}</span>
        <span className={classes.pausedBadge}>
          <svg width="10" height="10" viewBox="0 0 12 12" fill="#D4960B" aria-hidden="true">
            <rect x="2" y="1.5" width="3" height="9" rx=".8" />
            <rect x="7" y="1.5" width="3" height="9" rx=".8" />
          </svg>
          <FormattedText id="dashboard.paused.planCard.badge" />
        </span>
      </div>
      {copy.dateLabelId ? (
        <PausedPlanCardDateRow labelId={copy.dateLabelId} date={date} />
      ) : (
        <PausedPlanCardUsage sentCount={sentCount} limit={limit} />
      )}
      <div>
        <button
          type="button"
          className={classes.resumeButton}
          onClick={onCtaClick}
          disabled={isPaymentPaused && !isUpdateCardAvailable}
        >
          <FormattedText id={copy.ctaId} />
        </button>
      </div>
    </div>
  )
}

function PausedPlanCardUsage({ sentCount, limit }: PausedPlanCardUsageProps) {
  const usedPercent = limit > 0 ? Math.min((sentCount / limit) * 100, 100) : 100

  return (
    <div className={classes.usage}>
      <div className={classes.usageRow}>
        <FormattedText id="dashboard.paused.planCard.usedLabel" />
        <span className={classes.usageValue}>
          <FormattedText id="dashboard.paused.planCard.usedValue" values={{ used: sentCount, limit }} />
        </span>
      </div>
      <div className={classes.usageTrack}>
        <div className={classes.usageBar} style={{ width: `${usedPercent}%` }} />
      </div>
    </div>
  )
}

function PausedPlanCardDateRow({ labelId, date }: PausedPlanCardDateRowProps) {
  return (
    <div className={classes.usage}>
      <div className={classes.usageRow}>
        <FormattedText id={labelId} />
        <span className={classes.usageValue}>{date}</span>
      </div>
    </div>
  )
}
